/**
 * Thread context panel — the right-panel "context" surface.
 *
 * Ports the design branch's contextual panel visuals (tone-bordered action
 * cards, Finder-style file icon previews, Composio tool logos, tone badges)
 * onto REAL thread data. It renders three things, each from live client state
 * and each with a proper empty state — there is NO demo/scenario content:
 *
 *   1. Interaction cards ("For you") — folded from the thread's MorrowOS
 *      sub-agent roster + the subject's entitlements:
 *        · connection-needed  → the agent's `connectRequests`; the Connect
 *          button runs the real Composio connect flow.
 *        · error              → a failed sub-agent tool call; Reconnect runs the
 *          real connect flow for the offending app.
 *        · seat-limit         → the live entitlements seat gate; Add seats opens
 *          the real upgrade modal.
 *        · task-summary       → a completed sub-agent's consolidated answer.
 *      (The card model is a superset that also covers agent-emitted scenarios —
 *      decision / user-input / reply-draft / person-found — so the same renderer
 *      draws them once the backend emits them; nothing here fabricates them.)
 *   2. Files       — the attachments carried by the thread's messages.
 *   3. Apps &amp; tools — the tool calls the sub-agent roster actually made.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  AlertTriangleIcon,
  CheckCircle2Icon,
  CheckIcon,
  LayoutGridIcon,
  Loader2Icon,
  PaperclipIcon,
  SparklesIcon,
  BoxIcon,
  ZapIcon,
} from "lucide-react";

import type { MorrowAgent } from "@t3tools/client-runtime/state/morrowAgentRuntime";

import {
  fetchConnectedSlugs,
  openPendingConnectPopup,
  pointConnectPopup,
  resolveComposioUserId,
  startConnect,
} from "../../composioConnect";
import { useEntitlements } from "../../morrowEntitlements";
import { openUpgradeModal } from "../../upgradeModal";
import { cn } from "~/lib/utils";
import type { ChatAttachment } from "../../types";
import { MorrowAgentDetailSection } from "../MorrowAgentDetailPanel";
import { MorrowAgentOrb } from "../brand/MorrowAgentOrb";
import { formatToolCallContextBrief } from "../morrowToolCallBrief";
import { describeToolCall, isMorrowNativeToolSlug } from "../chat/workEntryStep";
import { useMorrowClerkAuth } from "../clerk/morrowClerkAuth";
import { Button } from "../ui/button";
import { ScrollArea } from "../ui/scroll-area";
import { formatBytes } from "../vault/vaultShared";
import { MorrowPersonAvatar } from "../workspace/MorrowPersonCard";
import { ContextFileIconPreview } from "./ContextFileIconPreview";
import { resolveContextToolIcon } from "./contextToolIcon";
import {
  buildConnectionCard,
  deriveConnectToolkits,
  deriveErrorCards,
  deriveSeatLimitCard,
  deriveTaskSummaryCard,
  sortContextCards,
  type ContextCard,
  type ContextCardIntent,
  type ContextCardOption,
  type ContextCardTone,
} from "./morrowContextCards";

export interface MorrowContextPanelProps {
  /** MorrowOS sub-agent roster for the active thread (folded tool activities). */
  readonly agents?: ReadonlyArray<MorrowAgent>;
  /** Attachments carried by the active thread's messages. */
  readonly attachments?: ReadonlyArray<ChatAttachment>;
  /** Sub-agent selected from the timeline — drives the activity section. */
  readonly selectedAgentId?: string | null;
}

type ToolStatus = "running" | "completed" | "failed";

interface ToolGroup {
  readonly key: string;
  readonly appName: string | null;
  readonly logoUrl: string | null;
  readonly actions: readonly string[];
  readonly hints: readonly string[];
  readonly count: number;
  readonly status: ToolStatus;
  readonly lastAt: string;
}

interface ContextFileItem {
  readonly id: string;
  readonly name: string;
  readonly mimeType: string;
  readonly sizeBytes: number;
  readonly previewUrl: string | null;
  readonly isImage: boolean;
}

const STATUS_TONE: Record<ToolStatus, { readonly badge: string; readonly label: string }> = {
  running: { badge: "bg-info/15 text-info", label: "Running" },
  completed: { badge: "bg-success/15 text-success", label: "Done" },
  failed: { badge: "bg-destructive/15 text-destructive", label: "Failed" },
};

function toneClasses(tone: ContextCardTone): {
  readonly badge: string;
  readonly border: string;
  readonly progress: string;
} {
  switch (tone) {
    case "success":
      return {
        badge: "bg-success/15 text-success",
        border: "border-success/30",
        progress: "bg-success",
      };
    case "warning":
      return {
        badge: "bg-warning/15 text-warning-foreground",
        border: "border-warning/35",
        progress: "bg-warning",
      };
    case "critical":
      return {
        badge: "bg-destructive/15 text-destructive",
        border: "border-destructive/35",
        progress: "bg-destructive",
      };
    case "info":
    default:
      return { badge: "bg-info/15 text-info", border: "border-info/25", progress: "bg-info" };
  }
}

/** App logo with Morrow mascot → letter-badge → generic fallback chain. */
function ContextToolIcon({
  logoUrl,
  appName,
  toolKey,
  size = "md",
}: {
  readonly logoUrl: string | null;
  readonly appName: string | null;
  readonly toolKey?: string;
  readonly size?: "sm" | "md";
}) {
  const [failed, setFailed] = useState(false);
  const sizeClass = size === "sm" ? "size-4" : "size-5";
  const useMorrowOrb =
    isMorrowNativeToolSlug(toolKey) || appName === "Morrow" || toolKey === "morrow";
  const useVaultIcon = toolKey === "vault" || toolKey?.includes("vault") || appName === "Vault";

  if (useMorrowOrb) {
    return <MorrowAgentOrb className={cn("shrink-0", sizeClass)} />;
  }

  if (useVaultIcon) {
    return (
      <span
        aria-hidden
        className={cn(
          "flex shrink-0 items-center justify-center rounded-[4px] bg-muted text-muted-foreground",
          sizeClass,
        )}
      >
        <BoxIcon className={size === "sm" ? "size-2.5" : "size-3"} />
      </span>
    );
  }

  if (logoUrl && !failed) {
    return (
      <img
        src={logoUrl}
        alt={appName ?? ""}
        className={cn("shrink-0 rounded-[4px] object-contain", sizeClass)}
        onError={() => setFailed(true)}
      />
    );
  }

  if (appName) {
    return (
      <span
        aria-hidden
        className={cn(
          "flex shrink-0 items-center justify-center rounded-[4px] bg-muted font-medium text-muted-foreground",
          sizeClass,
          size === "sm" ? "text-[8px]" : "text-[9px]",
        )}
      >
        {appName.slice(0, 1).toUpperCase()}
      </span>
    );
  }

  return (
    <span
      aria-hidden
      className={cn(
        "flex shrink-0 items-center justify-center rounded-[4px] bg-muted text-muted-foreground",
        sizeClass,
      )}
    >
      <BoxIcon className={size === "sm" ? "size-2.5" : "size-3"} />
    </span>
  );
}

/** Card icon: app logo from a toolkit slug, else the Morrow orb. */
function ContextCardToolIcon({
  toolSlug,
  className,
}: {
  readonly toolSlug?: string;
  readonly className?: string;
}) {
  const [failed, setFailed] = useState(false);
  const icon = toolSlug ? resolveContextToolIcon(toolSlug) : null;

  if (toolSlug && isMorrowNativeToolSlug(toolSlug)) {
    return <MorrowAgentOrb className={cn("size-6 shrink-0", className)} />;
  }

  if (toolSlug && icon?.logoUrl && !failed) {
    return (
      <img
        src={icon.logoUrl}
        alt={icon.appName ?? ""}
        className={cn("size-6 shrink-0 rounded-[5px] object-contain", className)}
        onError={() => setFailed(true)}
      />
    );
  }
  if (toolSlug && icon?.appName && icon.appName !== "Morrow") {
    return (
      <span
        aria-hidden
        className={cn(
          "flex size-6 shrink-0 items-center justify-center rounded-[5px] bg-muted text-[10px] font-medium text-muted-foreground",
          className,
        )}
      >
        {icon.appName.slice(0, 1).toUpperCase()}
      </span>
    );
  }
  return <MorrowAgentOrb className={cn("size-6 shrink-0", className)} />;
}

function ToolStatusBadge({ status }: { readonly status: ToolStatus }) {
  const tone = STATUS_TONE[status];
  return (
    <span
      className={cn(
        "flex shrink-0 items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-medium",
        tone.badge,
      )}
    >
      {status === "running" ? (
        <Loader2Icon aria-hidden className="size-2.5 animate-spin" />
      ) : status === "failed" ? (
        <AlertTriangleIcon aria-hidden className="size-2.5" />
      ) : (
        <CheckCircle2Icon aria-hidden className="size-2.5" />
      )}
      <span>{tone.label}</span>
    </span>
  );
}

function deriveToolGroups(agents: ReadonlyArray<MorrowAgent>): ToolGroup[] {
  const acc = new Map<
    string,
    {
      appName: string | null;
      logoUrl: string | null;
      actions: Set<string>;
      hints: Set<string>;
      count: number;
      running: number;
      failed: number;
      lastAt: string;
    }
  >();

  for (const agent of agents) {
    for (const call of agent.toolCalls) {
      const desc = describeToolCall(call.toolName);
      const key =
        desc.slug === "vault"
          ? call.toolName.trim().toLowerCase()
          : (desc.slug ?? desc.appName ?? call.toolName.trim().toLowerCase());
      const entry = acc.get(key) ?? {
        appName: desc.appName,
        logoUrl: desc.logoUrl,
        actions: new Set<string>(),
        hints: new Set<string>(),
        count: 0,
        running: 0,
        failed: 0,
        lastAt: "",
      };
      if (desc.action) entry.actions.add(desc.action);
      const hint = formatToolCallContextBrief(call);
      if (hint) entry.hints.add(hint);
      entry.count += 1;
      if (call.status === "running") entry.running += 1;
      if (call.status === "failed") entry.failed += 1;
      const at = call.completedAt ?? call.startedAt;
      if (at > entry.lastAt) entry.lastAt = at;
      acc.set(key, entry);
    }
  }

  return Array.from(acc.entries())
    .map(([key, entry]) => ({
      key,
      appName: entry.appName,
      logoUrl: entry.logoUrl,
      actions: Array.from(entry.actions),
      hints: Array.from(entry.hints),
      count: entry.count,
      status: (entry.running > 0
        ? "running"
        : entry.failed > 0
          ? "failed"
          : "completed") as ToolStatus,
      lastAt: entry.lastAt,
    }))
    .sort((a, b) => b.lastAt.localeCompare(a.lastAt));
}

function deriveFiles(attachments: ReadonlyArray<ChatAttachment>): ContextFileItem[] {
  const seen = new Set<string>();
  const files: ContextFileItem[] = [];
  for (const attachment of attachments) {
    if (seen.has(attachment.id)) continue;
    seen.add(attachment.id);
    files.push({
      id: attachment.id,
      name: attachment.name,
      mimeType: attachment.mimeType,
      sizeBytes: attachment.sizeBytes,
      previewUrl: attachment.previewUrl ?? null,
      isImage: attachment.type === "image",
    });
  }
  return files;
}

function SectionHeader({
  icon,
  label,
  count,
}: {
  readonly icon: React.ReactNode;
  readonly label: string;
  readonly count: number;
}) {
  return (
    <div className="mb-2 flex items-center gap-2">
      <span className="text-muted-foreground">{icon}</span>
      <p className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
        {label}
      </p>
      <span className="rounded-full bg-muted px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground">
        {count}
      </span>
    </div>
  );
}

function FileRow({ file }: { readonly file: ContextFileItem }) {
  return (
    <div className="flex items-center gap-3 rounded-xl border border-border/50 bg-background/60 p-2.5">
      {file.isImage && file.previewUrl ? (
        <img
          src={file.previewUrl}
          alt={file.name}
          className="size-9 shrink-0 rounded-lg border border-border/60 object-cover"
        />
      ) : (
        <ContextFileIconPreview
          file={{ name: file.name, mimeType: file.mimeType }}
          size="md"
          className="size-9"
        />
      )}
      <div className="min-w-0 flex-1">
        <p className="truncate text-[13px] font-medium text-foreground">{file.name}</p>
        <p className="truncate text-[11px] text-muted-foreground">
          {file.sizeBytes > 0 ? formatBytes(file.sizeBytes) : file.mimeType}
        </p>
      </div>
    </div>
  );
}

function ToolRow({ group }: { readonly group: ToolGroup }) {
  const primary = group.appName ?? group.actions[0] ?? "Tool";
  const detail =
    group.hints.length > 0
      ? group.hints.slice(0, 3).join(" · ")
      : group.appName && group.actions.length > 0
        ? group.actions.slice(0, 3).join(" · ")
        : group.actions.length > 1
          ? group.actions.slice(0, 3).join(" · ")
          : `${group.count} call${group.count === 1 ? "" : "s"}`;
  return (
    <div className="flex items-center gap-3 rounded-xl border border-border/50 bg-background/60 p-2.5">
      <ContextToolIcon
        logoUrl={group.logoUrl}
        appName={group.appName}
        toolKey={group.key}
        size="md"
      />
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <p className="truncate text-[13px] font-medium text-foreground">{primary}</p>
          {group.count > 1 ? (
            <span className="shrink-0 text-[11px] text-muted-foreground">×{group.count}</span>
          ) : null}
        </div>
        <p className="truncate text-[11px] text-muted-foreground">{detail}</p>
      </div>
      <ToolStatusBadge status={group.status} />
    </div>
  );
}

function defaultSelectedOptionId(options: readonly ContextCardOption[] | undefined): string | null {
  if (!options || options.length === 0) return null;
  return options.find((o) => o.recommended)?.id ?? options[0]?.id ?? null;
}

/** Renders a single interaction card. Buttons fire {@link ContextCardIntent}s. */
function ContextInteractionCard({
  card,
  onIntent,
}: {
  readonly card: ContextCard;
  readonly onIntent: (intent: ContextCardIntent | undefined) => void;
}) {
  const tone = toneClasses(card.tone);
  const [selectedOptionId, setSelectedOptionId] = useState<string | null>(() =>
    card.selectableOptions ? defaultSelectedOptionId(card.options) : null,
  );

  useEffect(() => {
    setSelectedOptionId(card.selectableOptions ? defaultSelectedOptionId(card.options) : null);
  }, [card.id, card.options, card.selectableOptions]);

  return (
    <div className={cn("flex flex-col rounded-2xl border bg-card/50 p-3.5", tone.border)}>
      <div className="flex items-start gap-3">
        {card.file ? (
          <ContextFileIconPreview file={card.file} size="md" className="mt-0.5 size-6" />
        ) : (
          <ContextCardToolIcon
            {...(card.toolSlug ? { toolSlug: card.toolSlug } : {})}
            className="mt-0.5"
          />
        )}
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-1.5">
            <h3 className="text-[14px] font-semibold leading-5 text-foreground">{card.title}</h3>
            {card.badge ? (
              <span
                className={cn(
                  "rounded-full px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide",
                  tone.badge,
                )}
              >
                {card.badge}
              </span>
            ) : null}
          </div>
          <p className="mt-1 text-[12.5px] leading-5 text-muted-foreground">{card.summary}</p>
        </div>
      </div>

      {(card.progress ||
        card.person ||
        (card.options && card.options.length > 0) ||
        (card.listItems && card.listItems.length > 0) ||
        card.detail) && (
        <div className="mt-3 flex flex-col gap-2.5">
          {card.progress ? (
            <div>
              <div className="mb-1.5 flex items-center justify-between text-[11px] text-muted-foreground">
                <span>{card.progress.label}</span>
                <span>{card.progress.value}%</span>
              </div>
              <div className="h-2 overflow-hidden rounded-full bg-muted">
                <div
                  className={cn("h-full rounded-full transition-all", tone.progress)}
                  style={{ width: `${card.progress.value}%` }}
                />
              </div>
            </div>
          ) : null}

          {card.person ? (
            <div className="flex items-center gap-3 rounded-xl border border-border/50 bg-background/60 p-3">
              <MorrowPersonAvatar name={card.person.name} size="md" />
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium">{card.person.name}</p>
                <p className="truncate text-[12px] text-muted-foreground">{card.person.email}</p>
                {card.person.role ? (
                  <p className="truncate text-[11px] text-muted-foreground/80">
                    {card.person.role}
                    {card.person.domain ? ` · ${card.person.domain}` : ""}
                  </p>
                ) : null}
              </div>
            </div>
          ) : null}

          {card.options && card.options.length > 0 ? (
            <div
              className="flex flex-col gap-2"
              role={card.selectableOptions ? "radiogroup" : undefined}
              aria-label={card.selectableOptions ? card.title : undefined}
            >
              {card.options.map((option) => {
                const isSelected = card.selectableOptions && selectedOptionId === option.id;
                return (
                  <button
                    key={option.id}
                    type="button"
                    role={card.selectableOptions ? "radio" : undefined}
                    aria-checked={card.selectableOptions ? isSelected : undefined}
                    onClick={() => {
                      if (card.selectableOptions) setSelectedOptionId(option.id);
                    }}
                    className={cn(
                      "rounded-xl border px-3 py-2.5 text-left transition-colors",
                      card.selectableOptions && "cursor-pointer",
                      isSelected
                        ? "border-primary/50 bg-primary/8 ring-1 ring-primary/25"
                        : "border-border/50 bg-background/50 hover:bg-muted/40",
                    )}
                  >
                    <div className="flex items-center gap-2.5">
                      {option.toolSlug ? (
                        <ContextCardToolIcon
                          toolSlug={option.toolSlug}
                          className="size-4 rounded-[4px]"
                        />
                      ) : null}
                      <span className="text-[13px] font-medium text-foreground">
                        {option.label}
                      </span>
                      {option.recommended ? (
                        <span className="rounded-full bg-primary/15 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-primary">
                          Recommended
                        </span>
                      ) : null}
                    </div>
                    {option.description ? (
                      <p className="mt-1 text-[12px] leading-5 text-muted-foreground">
                        {option.description}
                      </p>
                    ) : null}
                  </button>
                );
              })}
            </div>
          ) : null}

          {card.listItems && card.listItems.length > 0 ? (
            <div className="flex flex-col rounded-xl border border-border/50 bg-background/60 p-3">
              {card.listItemsLabel ? (
                <p className="mb-2.5 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                  {card.listItemsLabel}
                </p>
              ) : null}
              <ul className="space-y-2">
                {card.listItems.map((item) => (
                  <li
                    key={item}
                    className="flex items-start gap-2.5 text-[13px] leading-5 text-foreground/90"
                  >
                    <span className="mt-0.5 flex size-4 shrink-0 items-center justify-center rounded-full bg-success/15">
                      <CheckIcon aria-hidden className="size-2.5 text-success" />
                    </span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          ) : null}

          {card.detail ? (
            <div className="rounded-xl border border-border/50 bg-background/70 p-3">
              {card.detailLabel ? (
                <p className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
                  {card.detailLabel}
                </p>
              ) : null}
              <pre className="mt-1.5 whitespace-pre-wrap break-words font-mono text-[12px] leading-relaxed text-foreground/85">
                {card.detail}
              </pre>
            </div>
          ) : null}
        </div>
      )}

      {card.actions.length > 0 ? (
        <div className="mt-3.5 flex gap-2 border-t border-border/50 pt-3.5">
          {card.actions.map((action) => {
            const gatedByPick =
              card.selectableOptions && action.id === "confirm" && !selectedOptionId;
            return (
              <Button
                key={action.id}
                type="button"
                size="sm"
                variant={action.variant ?? "outline"}
                className="h-9 min-w-0 flex-1 px-2.5 text-[13px] font-medium"
                disabled={!action.intent || gatedByPick}
                onClick={() => onIntent(action.intent)}
              >
                <span className="truncate">{action.label}</span>
              </Button>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}

function EmptyState() {
  return (
    <div className="flex min-h-[16rem] flex-col items-center justify-center gap-3 px-6 text-center">
      <span className="flex size-11 items-center justify-center rounded-full bg-muted/60">
        <SparklesIcon aria-hidden className="size-5 text-muted-foreground" />
      </span>
      <div>
        <p className="text-[13px] font-medium text-foreground">No context yet</p>
        <p className="mt-1 text-[12px] leading-5 text-muted-foreground">
          Files you attach, the apps Morrow uses, and anything it needs from you in this thread will
          show up here.
        </p>
      </div>
    </div>
  );
}

/**
 * Self-contained Composio connect flow (mirrors the in-chat connect card): opens
 * the OAuth popup synchronously on click, then polls until the toolkit connects.
 * Returns per-toolkit state plus a `connect(slug)` runner the cards call.
 */
function useToolkitConnect(toolkits: readonly string[]) {
  const {
    isLoaded: isClerkLoaded,
    isSignedIn,
    userId: clerkUserId,
    openSignIn,
  } = useMorrowClerkAuth();
  const userId = useMemo(
    () => resolveComposioUserId(isClerkLoaded && isSignedIn ? clerkUserId : null),
    [isClerkLoaded, isSignedIn, clerkUserId],
  );
  const [connecting, setConnecting] = useState<ReadonlySet<string>>(() => new Set());
  const [connected, setConnected] = useState<ReadonlySet<string>>(() => new Set());
  const pollsRef = useRef<Map<string, number>>(new Map());

  const key = toolkits.join(",");
  // Prime "already connected" so a slug the agent asked for but which is in fact
  // connected reads as done (no stale "Connect" prompt).
  useEffect(() => {
    if (toolkits.length === 0) return;
    let cancelled = false;
    void fetchConnectedSlugs(userId).then((slugs) => {
      if (cancelled || !slugs) return; // null = unknown/failed → leave state untouched
      const set = new Set(slugs.map((s) => s.toLowerCase()));
      setConnected((prev) => {
        const next = new Set(prev);
        for (const slug of toolkits) if (set.has(slug)) next.add(slug);
        return next;
      });
    });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, userId]);

  useEffect(() => {
    const polls = pollsRef.current;
    return () => {
      for (const id of polls.values()) window.clearInterval(id);
      polls.clear();
    };
  }, []);

  const connect = useCallback(
    (toolkit: string) => {
      if (isClerkLoaded && !isSignedIn) {
        openSignIn();
        return;
      }
      setConnecting((prev) => new Set(prev).add(toolkit));
      const clearConnecting = () =>
        setConnecting((prev) => {
          const next = new Set(prev);
          next.delete(toolkit);
          return next;
        });
      const pendingPopup = openPendingConnectPopup();
      void (async () => {
        try {
          const outcome = await startConnect(toolkit, userId);
          if (outcome.kind === "failed") {
            pendingPopup?.close();
            clearConnecting();
            return;
          }
          if (outcome.kind === "connected") {
            pendingPopup?.close();
            setConnected((prev) => new Set(prev).add(toolkit));
            clearConnecting();
            return;
          }
          const popup = pointConnectPopup(pendingPopup, outcome.url);
          let ticks = 0;
          const maxTicks = 60; // ~3 min at 3s
          const poll = window.setInterval(() => {
            void (async () => {
              ticks += 1;
              const slugs = await fetchConnectedSlugs(userId);
              const done = (slugs ?? []).map((s) => s.toLowerCase()).includes(toolkit);
              const popupClosed = popup?.closed ?? false;
              if (done || ticks >= maxTicks || (popupClosed && ticks > 1)) {
                window.clearInterval(poll);
                pollsRef.current.delete(toolkit);
                if (done) setConnected((prev) => new Set(prev).add(toolkit));
                clearConnecting();
              }
            })();
          }, 3000);
          pollsRef.current.set(toolkit, poll);
        } catch {
          pendingPopup?.close();
          clearConnecting();
        }
      })();
    },
    [isClerkLoaded, isSignedIn, openSignIn, userId],
  );

  return { connect, connecting, connected };
}

export function MorrowContextPanel({
  agents,
  attachments,
  selectedAgentId = null,
}: MorrowContextPanelProps = {}) {
  const roster = useMemo(() => agents ?? [], [agents]);
  const toolGroups = useMemo(() => deriveToolGroups(roster), [roster]);
  const files = useMemo(() => deriveFiles(attachments ?? []), [attachments]);

  const entitlements = useEntitlements();
  const [dismissed, setDismissed] = useState<ReadonlySet<string>>(() => new Set());

  const connectToolkits = useMemo(() => deriveConnectToolkits(roster), [roster]);
  const { connect, connecting, connected } = useToolkitConnect(connectToolkits);

  const runIntent = useCallback(
    (intent: ContextCardIntent | undefined, cardId: string) => {
      if (!intent) return;
      switch (intent.type) {
        case "dismiss":
          setDismissed((prev) => new Set(prev).add(cardId));
          return;
        case "connect":
          connect(intent.toolkit);
          return;
        case "upgrade":
          openUpgradeModal({ capability: intent.capability });
          return;
      }
    },
    [connect],
  );

  const cards = useMemo(() => {
    const list: ContextCard[] = [];

    // Connection-needed — the agent asked to connect these apps. A slug already
    // connected shows a brief "connected" confirmation instead of a prompt.
    for (const toolkit of connectToolkits) {
      const card = buildConnectionCard(toolkit, {
        connecting: connecting.has(toolkit),
        connected: connected.has(toolkit),
      });
      if (!dismissed.has(card.id)) list.push(card);
    }

    // Errors — failed sub-agent tool calls.
    for (const card of deriveErrorCards(roster)) {
      if (!dismissed.has(card.id)) list.push(card);
    }

    // Seat limit — live entitlements gate.
    if (entitlements.isLoaded) {
      const seats = entitlements.remaining("seats");
      const seatCard = deriveSeatLimitCard({
        isOrg: entitlements.viewer.isOrg,
        used: seats.used,
        limit: seats.limit,
        remaining: seats.remaining,
      });
      if (seatCard && !dismissed.has(seatCard.id)) list.push(seatCard);
    }

    // Task summary — a completed sub-agent's consolidated answer.
    const summary = deriveTaskSummaryCard(roster);
    if (summary && !dismissed.has(summary.id)) list.push(summary);

    return sortContextCards(list);
  }, [connectToolkits, connecting, connected, dismissed, roster, entitlements]);

  const isEmpty =
    cards.length === 0 && toolGroups.length === 0 && files.length === 0 && roster.length === 0;

  return (
    <div className="flex h-full min-h-0 flex-col">
      <ScrollArea className="min-h-0 flex-1" scrollFade>
        {isEmpty ? (
          <EmptyState />
        ) : (
          <div className="flex flex-col gap-5 p-4">
            {cards.length > 0 ? (
              <section>
                <SectionHeader
                  icon={<ZapIcon aria-hidden className="size-3.5" />}
                  label="For you"
                  count={cards.length}
                />
                <div className="flex flex-col gap-2.5">
                  {cards.map((card) => (
                    <ContextInteractionCard
                      key={card.id}
                      card={card}
                      onIntent={(intent) => runIntent(intent, card.id)}
                    />
                  ))}
                </div>
              </section>
            ) : null}

            {files.length > 0 ? (
              <section>
                <SectionHeader
                  icon={<PaperclipIcon aria-hidden className="size-3.5" />}
                  label="Files"
                  count={files.length}
                />
                <div className="flex flex-col gap-2">
                  {files.map((file) => (
                    <FileRow key={file.id} file={file} />
                  ))}
                </div>
              </section>
            ) : null}

            {toolGroups.length > 0 ? (
              <section>
                <SectionHeader
                  icon={<LayoutGridIcon aria-hidden className="size-3.5" />}
                  label="Apps & tools"
                  count={toolGroups.length}
                />
                <div className="flex flex-col gap-2">
                  {toolGroups.map((group) => (
                    <ToolRow key={group.key} group={group} />
                  ))}
                </div>
              </section>
            ) : null}

            {roster.length > 0 ? (
              <MorrowAgentDetailSection agents={roster} selectedAgentId={selectedAgentId} />
            ) : null}
          </div>
        )}
      </ScrollArea>
    </div>
  );
}
